import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { billingApi, type SubscriptionState, type BillingSubscription, type BillingUsage } from '@/lib/api'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function planLabel(planId: string | null): string {
  if (!planId) return 'Free'
  const MAP: Record<string, string> = {
    premium_monthly: 'Premium (Monthly)',
    premium_annual:  'Premium (Annual)',
    api_developer:   'Developer API',
    api_business:    'Business API',
  }
  return MAP[planId] ?? planId
}

function stateBadge(state: SubscriptionState) {
  const CLASSES: Record<SubscriptionState, string> = {
    free:      'bg-star-dim/10 text-star-dim',
    trialing:  'bg-violet/15 text-violet-glow',
    active:    'bg-auspicious/15 text-auspicious',
    past_due:  'bg-inauspicious/15 text-inauspicious',
    cancelled: 'bg-star-dim/10 text-star-dim',
  }
  const LABELS: Record<SubscriptionState, string> = {
    free:      'Free',
    trialing:  'Trial',
    active:    'Active',
    past_due:  'Past Due',
    cancelled: 'Cancelled',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-medium ${CLASSES[state]}`}>
      {LABELS[state]}
    </span>
  )
}

// ─── Current Plan Card ────────────────────────────────────────────────────────

function CurrentPlanCard({ sub }: { sub: BillingSubscription }) {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)

  async function openPortal() {
    setPortalError(null)
    setPortalLoading(true)
    try {
      const { portalUrl } = await billingApi.portal()
      window.location.href = portalUrl
    } catch {
      setPortalError('Unable to open billing portal. Please try again.')
      setPortalLoading(false)
    }
  }

  return (
    <div className="card-cosmic p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-star-dim text-xs font-mono mb-1">Current plan</p>
          <div className="flex items-center gap-2.5">
            <h2 className="text-star-white text-xl font-semibold">{planLabel(sub.planId)}</h2>
            {stateBadge(sub.state)}
          </div>
        </div>
        {sub.state === 'free' && (
          <Link
            to="/pricing"
            className="shrink-0 px-4 py-2 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white text-sm font-medium hover:shadow-glow-violet transition-all"
          >
            Upgrade →
          </Link>
        )}
        {sub.state === 'cancelled' && (
          <Link
            to="/pricing"
            className="shrink-0 px-4 py-2 rounded-lg border border-violet text-violet-glow text-sm font-medium hover:bg-violet/10 transition-all"
          >
            Resubscribe →
          </Link>
        )}
      </div>

      {/* State-specific banners */}
      {sub.state === 'past_due' && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg bg-inauspicious/10 border border-inauspicious/30 text-inauspicious text-sm"
        >
          <span className="text-lg leading-none shrink-0">⚠</span>
          <div>
            <p className="font-medium">Payment past due</p>
            <p className="text-xs mt-0.5 opacity-80">Your subscription is at risk. Please update your payment method.</p>
          </div>
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="ml-auto shrink-0 text-xs font-medium underline opacity-80 hover:opacity-100 disabled:opacity-50 transition-opacity"
          >
            {portalLoading ? 'Opening…' : 'Fix payment →'}
          </button>
        </div>
      )}

      {sub.state === 'trialing' && sub.trialEndsAt && (
        <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-lg bg-violet/10 border border-violet/25 text-violet-glow text-sm">
          <span className="text-lg leading-none">✦</span>
          <p>
            Free trial ends{' '}
            <strong>{new Date(sub.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</strong>
            {' '}— no charge until then.
          </p>
        </div>
      )}

      {/* Plan details grid */}
      {sub.state !== 'free' && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {sub.price != null && (
            <div>
              <dt className="text-star-dim text-xs font-mono">Price</dt>
              <dd className="text-star-white font-semibold mt-0.5">{sub.price}</dd>
            </div>
          )}
          {sub.nextBillingDate && (
            <div>
              <dt className="text-star-dim text-xs font-mono">Next billing</dt>
              <dd className="text-star-white mt-0.5">
                {new Date(sub.nextBillingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </dd>
            </div>
          )}
          {sub.cancelAtPeriodEnd && (
            <div>
              <dt className="text-star-dim text-xs font-mono">Access until</dt>
              <dd className="text-star-white mt-0.5">
                {sub.nextBillingDate
                  ? new Date(sub.nextBillingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—'}
              </dd>
            </div>
          )}
        </dl>
      )}

      {portalError && (
        <p role="alert" className="text-inauspicious text-xs mb-4">{portalError}</p>
      )}

      {/* Actions */}
      {(sub.state === 'active' || sub.state === 'trialing') && (
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/pricing"
            className="text-sm px-4 py-2 rounded-lg border border-cosmic-mist text-star-soft hover:border-violet-dark hover:text-star-bright transition-all"
          >
            Change plan
          </Link>
          {!sub.cancelAtPeriodEnd && (
            <button
              onClick={() => setCancelOpen(true)}
              className="text-sm px-4 py-2 rounded-lg border border-cosmic-mist text-star-dim hover:border-inauspicious/50 hover:text-inauspicious transition-all"
            >
              Cancel subscription
            </button>
          )}
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="text-sm px-4 py-2 rounded-lg border border-cosmic-mist text-star-soft hover:border-violet-dark hover:text-star-bright disabled:opacity-60 transition-all"
          >
            {portalLoading ? 'Opening…' : 'Billing history & invoices →'}
          </button>
        </div>
      )}

      {sub.state === 'past_due' && (
        <button
          onClick={openPortal}
          disabled={portalLoading}
          className="text-sm px-4 py-2 rounded-lg bg-inauspicious/80 text-star-white hover:bg-inauspicious disabled:opacity-60 transition-colors"
        >
          {portalLoading ? 'Opening…' : 'Update payment method →'}
        </button>
      )}

      {/* Cancel confirmation dialog */}
      {cancelOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cancel subscription"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-cosmic-void/80 backdrop-blur-sm"
        >
          <div className="card-cosmic p-6 max-w-md w-full space-y-4">
            <h3 className="text-star-white font-semibold">Cancel subscription?</h3>
            <p className="text-star-soft text-sm leading-relaxed">
              You'll keep access until the end of the billing period. After that, your account
              reverts to the free tier — your chart data is always kept.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={openPortal}
                disabled={portalLoading}
                className="px-4 py-2 rounded-lg bg-inauspicious/80 text-star-white text-sm hover:bg-inauspicious disabled:opacity-60 transition-colors"
              >
                {portalLoading ? 'Opening…' : 'Yes, cancel'}
              </button>
              <button
                onClick={() => setCancelOpen(false)}
                className="px-4 py-2 rounded-lg border border-cosmic-mist text-star-soft text-sm hover:text-star-bright transition-colors"
              >
                Keep subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Usage Card ───────────────────────────────────────────────────────────────

function UsageCard({ usage }: { usage: BillingUsage }) {
  const pct = usage.limit === 0 ? 0 : Math.min(100, Math.round((usage.used / usage.limit) * 100))
  const isWarning = pct >= 80
  const isOver = pct >= 100

  return (
    <div className="card-cosmic p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-star-bright font-medium">API Usage</h3>
        <span className="text-star-dim text-xs font-mono">{usage.period}</span>
      </div>

      <div>
        <div className="flex items-end justify-between mb-2">
          <span className="text-star-white font-semibold tabular-nums">
            {usage.used.toLocaleString()}
            <span className="text-star-dim font-normal"> / {usage.limit === 0 ? '∞' : usage.limit.toLocaleString()}</span>
          </span>
          {usage.limit > 0 && (
            <span className={`text-xs font-mono ${isOver ? 'text-inauspicious' : isWarning ? 'text-gold' : 'text-star-dim'}`}>
              {pct}%
            </span>
          )}
        </div>
        {usage.limit > 0 && (
          <div className="w-full h-2 rounded-full bg-cosmic-mist/30 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="API usage">
            <div
              className={`h-full rounded-full transition-all ${isOver ? 'bg-inauspicious' : isWarning ? 'bg-gold' : 'bg-violet'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {usage.limit === 0 && (
          <p className="text-star-dim text-xs">Unlimited requests on your plan.</p>
        )}
      </div>

      {isOver && (
        <p role="alert" className="text-inauspicious text-xs">
          You've reached your monthly limit.{' '}
          <Link to="/pricing" className="underline hover:text-inauspicious/80 transition-colors">Upgrade for more →</Link>
        </p>
      )}
      {isWarning && !isOver && (
        <p className="text-gold text-xs">
          Approaching your limit. Consider upgrading before the month ends.
        </p>
      )}

      {usage.resetDate && (
        <p className="text-star-dim text-xs font-mono">
          Resets {new Date(usage.resetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </p>
      )}
    </div>
  )
}

// ─── Free Upgrade CTA ─────────────────────────────────────────────────────────

function UpgradeCta() {
  return (
    <div className="card-cosmic p-8 text-center border-violet/30">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-deep/50 border border-violet/20 mb-4">
        <span className="text-gold text-xl">✦</span>
      </div>
      <h3 className="text-star-white font-semibold mb-2">Unlock everything</h3>
      <p className="text-star-soft text-sm max-w-xs mx-auto mb-6 leading-relaxed">
        Unlimited Muhurta lookups, full Dasha interpretation, compatibility analysis, calendar export, and more.
      </p>
      <Link
        to="/pricing"
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium text-sm hover:shadow-glow-violet transition-all"
      >
        Start free 7-day trial
      </Link>
      <p className="text-star-dim text-xs mt-3">No credit card required.</p>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function BillingPage() {
  const { data: sub, isLoading: subLoading, isError: subError, refetch: refetchSub } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription(),
    retry: 1,
    staleTime: 60_000,
  })

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['billing', 'usage'],
    queryFn: () => billingApi.getUsage(),
    retry: 1,
    staleTime: 60_000,
  })

  const isLoading = subLoading || usageLoading

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-star-white text-2xl font-semibold">Billing</h1>
        <p className="text-star-dim text-sm mt-1">Manage your subscription and usage.</p>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="card-cosmic p-6 space-y-4">
            <div className="shimmer h-3 w-24 rounded-md" />
            <div className="shimmer h-7 w-44 rounded-md" />
            <div className="shimmer h-10 w-28 rounded-lg mt-4" />
          </div>
          <div className="card-cosmic p-6 space-y-3">
            <div className="shimmer h-3 w-20 rounded-md" />
            <div className="shimmer h-2 w-full rounded-full" />
            <div className="shimmer h-3 w-32 rounded-md" />
          </div>
        </div>
      ) : subError || !sub ? (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-inauspicious/10 border border-inauspicious/20">
          <span className="text-sm text-inauspicious">Could not load billing information.</span>
          <button onClick={() => refetchSub()} className="text-xs text-gold hover:text-gold-bright transition-colors">Retry</button>
        </div>
      ) : (
        <>
          <CurrentPlanCard sub={sub} />
          {usage && <UsageCard usage={usage} />}
          {sub.state === 'free' && <UpgradeCta />}
        </>
      )}
    </div>
  )
}
